import type { LabelProps } from 'recharts';

import { AXIS_TITLE_RESERVE, AXIS_TITLE_STYLE } from './axisTitleStyle.ts';

/** Cartesian axis box — the relevant slice of recharts' `ViewBox` for an X/Y axis title. */
type AxisViewBox = { readonly x?: number; readonly y?: number; readonly width?: number; readonly height?: number };

const UNIT_GAP = 6;

/**
 * Custom SVG axis title: the declared label in the standard axis-title
 * colour, followed by the unit in a dimmed, lighter tone so the two read
 * as title + qualifier. Side (Y) axes rotate -90° about their anchor.
 */
export function AxisTitle({
  viewBox,
  title,
  unit,
  placement,
}: {
  viewBox?: LabelProps['viewBox'];
  title: string;
  unit: string;
  placement: 'bottom' | 'left' | 'right';
}) {
  // Axis labels are always cartesian, so the box carries x/y/width/height.
  const { x = 0, y = 0, width = 0, height = 0 } = (viewBox ?? {}) as AxisViewBox;
  // Rotated (side) titles are centred in the reserved margin strip *outside*
  // the axis band — a disjoint x-region from the auto-fit tick values — so
  // they clear the ticks at any magnitude. Bottom titles sit inside the
  // axis box, which already reserves vertical room via its height.
  const anchor = {
    bottom: { cx: x + width / 2, cy: y + height - 4, rotate: 0 },
    left: { cx: x - AXIS_TITLE_RESERVE / 2, cy: y + height / 2, rotate: -90 },
    right: { cx: x + width + AXIS_TITLE_RESERVE / 2, cy: y + height / 2, rotate: -90 },
  }[placement];
  return (
    <text
      dominantBaseline={placement === 'bottom' ? 'auto' : 'central'}
      fontSize={AXIS_TITLE_STYLE.fontSize}
      textAnchor="middle"
      transform={anchor.rotate === 0 ? undefined : `rotate(${anchor.rotate}, ${anchor.cx}, ${anchor.cy})`}
      x={anchor.cx}
      y={anchor.cy}
    >
      {title === '' ? null : <tspan fill={AXIS_TITLE_STYLE.fill}>{title}</tspan>}
      <tspan dx={title === '' ? 0 : UNIT_GAP} fill="var(--mantine-color-dimmed)">
        {unit}
      </tspan>
    </text>
  );
}
